import { FC, useCallback } from 'react';
import { Form, Formik } from 'formik';
import { useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import TextField from 'components/TextField';
import IconSelect from 'components/IconSelect';
import AgreementBlockSelect from 'components/AgreementBlockSelect';

import { useDialog } from 'components/Dialog';
import { useErrorHandler } from 'lib';
import {
  AgreementBlock,
  TermsListAddMutation,
} from '__generated__/TermsListAddMutation.graphql';

type FormFields = {
  name: string;
  icon: string | null;
  agreementBlock: AgreementBlock | null;
};

type Props = {
  connectionId: string;
};

const initial = {
  name: '',
  icon: null,
  agreementBlock: null,
};

const TermsListAdd: FC<Props> = ({ connectionId }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const [add, adding] = useMutation<TermsListAddMutation>(graphql`
    mutation TermsListAddMutation(
      $input: CreateTermInput!
      $connections: [ID!]!
    ) {
      createTerm(input: $input) {
        node @prependNode(connections: $connections, edgeTypeName: "TermEdge") {
          ...TermsListRow_term
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: { connections: [connectionId], input },
        ...errorHandler({
          onCompleted: () => {
            close();
          },
        }),
      });
    },
    [add, close, connectionId, errorHandler]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>
          Nauja įskaičiuota/neįskaičiuota į kainą sąlyga
        </DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują įskaičiuota/neįskaičiuota į kainą sąlygą. Vėliau
            šias sąlygas galima naudoti redaguojant kelionės pasiūlymus
          </DialogContentText>

          <Stack spacing={4}>
            <TextField autoFocus fullWidth name="name" label="Pavadinimas" />
            <IconSelect fullWidth name="icon" label="Ikona" />

            <AgreementBlockSelect
              name="agreementBlock"
              label="Įrašyti į sutartį prie"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default TermsListAdd;
